import { transformToCamelCaseDeep, transformToSnakeCase } from '@gw/ui';

import { getConfig } from '@/config/runtimeConfig';
import type {
  LaunchOSINTRequest,
  LaunchOSINTResponse,
  ListOSINTApplicationsResponse,
  ListOSINTSessionsResponse,
  OSINTSession,
  PollOSINTResponse,
  SessionAccessWireResponse,
  LaunchSessionWireResponse,
  OSINTSessionWireResponse,
} from '@/types/osint.types';

import { BaseApiClient, type RequestOptions } from './baseApiClient';

/**
 * Pick the access URL from wire response based on access_mode
 */
function pickAccessUrl(wire: {
  access_mode: string;
  terminal_websocket_url: string | null;
  web_ui_launch_url: string | null;
}): string | null {
  if (wire.access_mode === 'TERMINAL') {
    return wire.terminal_websocket_url;
  }
  return wire.web_ui_launch_url;
}

class OSINTApiClient extends BaseApiClient {
  constructor() {
    // moarApiUrl is the base of the gw-moar ALB (e.g. https://osint.dev.generalwisdom.com/api).
    // ALB routes: /applications → Lambda applications handler, /sessions/* → Lambda provisioner.
    // GW-6235: suppress global session-expired emission. gw-moar authenticates via session_jwt
    // (passed as customAuthToken on session calls), not Cognito. A 401 from gw-moar means the
    // moar session JWT is invalid/expired — it must NOT sign the user out of the GW platform.
    // Callers handle these errors inline (e.g. ContainerLaunchPanel toast, ContainerSessionPage).
    // GW-6394: Use baseURLFactory to defer getConfig() until first API call.
    super({ baseURLFactory: () => getConfig().moarApiUrl ?? '', suppressSessionExpired: true });
  }

  async listApplications(): Promise<ListOSINTApplicationsResponse> {
    const wire = await this.get<{ applications: unknown[] }>('/applications');
    // Deep transform handles the nested applications array
    return transformToCamelCaseDeep(wire) as ListOSINTApplicationsResponse;
  }

  async launchSession(
    request: LaunchOSINTRequest,
    options?: RequestOptions
  ): Promise<LaunchOSINTResponse> {
    // Convert request to snake_case
    const snakeRequest = transformToSnakeCase(request);
    const wire = await this.post<LaunchSessionWireResponse>('/sessions', snakeRequest, options);

    // Remap response: status → taskStatus
    return {
      sessionId: wire.session_id,
      taskArn: wire.task_arn ?? '',
      taskStatus: wire.status as LaunchOSINTResponse['taskStatus'],
      accessMode: (wire.access_mode ?? 'WEB_UI') as LaunchOSINTResponse['accessMode'],
      expiresAt: wire.expires_at ?? '',
    };
  }

  async pollSession(sessionId: string, options?: RequestOptions): Promise<PollOSINTResponse> {
    const wire = await this.get<SessionAccessWireResponse>(`/sessions/${sessionId}/poll`, options);

    // Remap: status → taskStatus, pick accessUrl by mode
    return {
      sessionId: wire.session_id,
      taskStatus: wire.status as PollOSINTResponse['taskStatus'],
      accessUrl: pickAccessUrl(wire),
      taskArn: '', // poll response doesn't include task_arn
    };
  }

  async getSession(sessionId: string, options?: RequestOptions): Promise<OSINTSession> {
    const wire = await this.get<OSINTSessionWireResponse>(`/sessions/${sessionId}`, options);

    // Full remap including task_arn → taskArn, task_status → taskStatus
    return {
      sessionId: wire.session_id,
      userId: wire.user_id,
      appId: wire.app_id,
      taskArn: wire.task_arn,
      taskStatus: wire.task_status as OSINTSession['taskStatus'],
      accessMode: wire.access_mode as OSINTSession['accessMode'],
      accessUrl: pickAccessUrl(wire),
      expiresAt: wire.expires_at,
      createdAt: wire.created_at,
      terminatedAt: wire.terminated_at,
    };
  }

  async listSessions(options?: RequestOptions): Promise<ListOSINTSessionsResponse> {
    const wire = await this.get<{ sessions: OSINTSessionWireResponse[] }>('/sessions', options);

    // Map each session from wire to SPA shape
    const sessions = wire.sessions.map((s) => ({
      sessionId: s.session_id,
      userId: s.user_id,
      appId: s.app_id,
      taskArn: s.task_arn,
      taskStatus: s.task_status as OSINTSession['taskStatus'],
      accessMode: s.access_mode as OSINTSession['accessMode'],
      accessUrl: pickAccessUrl(s),
      expiresAt: s.expires_at,
      createdAt: s.created_at,
      terminatedAt: s.terminated_at,
    }));

    return { sessions };
  }

  async terminateSession(sessionId: string, options?: RequestOptions): Promise<void> {
    return this.delete<void>(`/sessions/${sessionId}`, options);
  }
}

export const osintApi = new OSINTApiClient();
